import React, { useEffect, useRef } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "providers/Auth";
import { useGlobalConfig } from "providers/GlobalConfig";
import { auth as firebaseAuth } from "firebaseClient";
import { GlobalConfigType, UserConfig } from "types/configs";
import {
  ONBOARDING_STEPS,
  ENROLL_PATH,
  SELF_SERVE_TENANT_ID,
} from "types/onboarding";
import { AuthStatus } from "types/auth";

const REDIRECT_TO = `/`;

const getNextIncompleteStep = (userConfig: UserConfig) => {
  const currentStep =
    userConfig?.props?.onboarding?.currentStep ?? ONBOARDING_STEPS.survey;

  if (currentStep === ONBOARDING_STEPS.survey)
    return `${ENROLL_PATH}/${ONBOARDING_STEPS.survey}`;
  if (currentStep === ONBOARDING_STEPS.catalog)
    return `${ENROLL_PATH}/${ONBOARDING_STEPS.catalog}`;
  if (currentStep === ONBOARDING_STEPS.verifyEmail)
    return `${ENROLL_PATH}/${ONBOARDING_STEPS.verifyEmail}`;
  return REDIRECT_TO;
};

export const SelfServeGuard: React.FC = () => {
  const { auth } = useAuth()!;
  const { globalConfig, loadConfig } = useGlobalConfig()! as GlobalConfigType;
  const location = useLocation();
  const configLoadTriggered = useRef<string | null>(null);

  const isFinalizing = auth.onboardingMode === ONBOARDING_STEPS.finalizing;
  const isInitializing = auth.onboardingMode === ONBOARDING_STEPS.initializing;

  useEffect(() => {
    const currentUser = firebaseAuth.currentUser;
    const tenant = auth.tenant;
    if (!currentUser || !tenant) return;

    const key = `${tenant}::${currentUser.email || ""}`;
    if (configLoadTriggered.current === key) return;

    const cfgTenant = globalConfig.tenant?.tenant;
    const hasUser = !!globalConfig.user;

    if (!hasUser || cfgTenant !== tenant) {
      configLoadTriggered.current = key;
      void loadConfig(currentUser, tenant);
    }
  }, [auth.tenant, globalConfig.user, globalConfig.tenant, loadConfig]);

  if (isFinalizing || isInitializing) {
    return <Outlet />;
  }

  if (
    auth.status === AuthStatus.LOADING_CLAIMS ||
    auth.status === AuthStatus.INITIALIZING ||
    auth.status !== AuthStatus.READY
  ) {
    return null; // wait, don't redirect yet
  }

  if (auth.tenant !== SELF_SERVE_TENANT_ID) {
    return <Navigate to="/" replace />;
  }

  if (!globalConfig.user) return null;

  const nextStep = getNextIncompleteStep(globalConfig.user);
  const currentPath = location.pathname;

  if (currentPath.startsWith(nextStep) || currentPath === nextStep) {
    return <Outlet />;
  }

  return <Navigate to={nextStep} replace />;
};
